<template>
    <div class="slide" @click="$emit('click')">
        <div class="timestamp-bar" v-if="!isCustomTheme && $props.isSet">
            <font-awesome-icon icon="fa-regular fa-video" style="font-weight: 700"></font-awesome-icon>
            <span class="time-stamp">
                <slot name="timeslot">
                    {{ formatTimeStamp(duration) }}
                </slot>
            </span>
        </div>
        <div class="overlay-extras">
            <slot name="topRightCorner"> </slot>
        </div>

        <div
            class="slide-image"
            :class="{ loaded: previewVideoLoaded, 'custom-theme': isCustomTheme, unset: !$props.isSet }"
            @mouseenter="
                () => {
                    if ($props.isSet) {
                        hasMousedOver = true;
                    }
                }
            "
        >
            <slot name="image">
                <template v-if="isCustomTheme">
                    <div class="icon-container">
                        <font-awesome-icon class="custom-theme-icon" icon="fa-solid fa-palette"></font-awesome-icon>
                    </div>
                    <video
                        src="../../../assets/images/custom-theme-thumb.mp4"
                        loop
                        class="slide-preview"
                        ref="videoPreview"
                        @loadstart="loadPreview"
                        @load="previewLoaded"
                        @mouseenter="play"
                        @mouseleave="pause"
                        autoplay
                        v-if="!state.isMobile"
                    />
                    <img v-else src="../../../assets/images/custom-theme-thumb.png" class="slide-preview" />
                </template>
                <template v-else-if="$props.tributeEndingVideo && $props.isSet">
                    <ImageThumbnail
                        :src="$props.tributeEndingVideo"
                        preset="small"
                        :type="1"
                        style="width: 100%; height: 100%; object-fit: cover"
                    />
                    <video
                        v-if="!state.isMobile"
                        :src="$props.tributeEndingVideo"
                        loop
                        class="slide-preview"
                        ref="videoPreview"
                        @loadstart="loadPreview"
                        @load="previewLoaded"
                        @mouseenter="play"
                        @mouseleave="pause"
                        muted
                        autoplay
                    />
                </template>
                <template v-else>
                    <img :src="thumbnail" />
                    <video
                        v-if="!state.isMobile && hasMousedOver"
                        :src="previewVideo"
                        preload="none"
                        muted
                        class="slide-preview"
                        ref="videoPreview"
                        @loadstart="loadPreview"
                        @load="previewLoaded"
                        @mouseenter="play"
                        @mouseleave="pause"
                        loop
                    ></video>
                </template>
            </slot>
        </div>

        <div v-if="staffFavorite" class="staff-picked">
            <CrownIcon />
            <span v-if="!state.isMobile">STAFF PICKED</span>
        </div>

        <div class="template-overlay">
            <div class="template-title">
                <slot name="title">{{ title }}</slot>
            </div>

            <div class="button-group">
                <slot name="actions">
                    <CustomTooltip :tooltipProps="tooltipProps">
                        <template #activator>
                            <button
                                v-if="!isSelected"
                                :disabled="loading || $attrs.disabled"
                                @click.stop="$emit(isCustomTheme ? 'selectCustom' : 'select')"
                                class="template-button white-button"
                            >
                                <font-awesome-icon icon="fa-regular fa-plus"></font-awesome-icon>
                            </button>
                        </template>

                        <template #content>
                            <span>Select</span>
                        </template>
                    </CustomTooltip>

                    <div class="button-group" v-if="!state.isMobile && template.title !== 'No Theme'">
                        <CustomTooltip :tooltipProps="tooltipProps">
                            <template #activator>
                                <button
                                    v-if="isSelected"
                                    @click.stop="isCustomTheme ? $emit('custom-theme-edit') : $emit('edit')"
                                    class="template-button white-button"
                                    :disabled="$attrs.disabled"
                                >
                                    <font-awesome-icon icon="fa-regular fa-pencil"></font-awesome-icon>
                                </button>
                            </template>
                            <template #content>
                                <span>Edit</span>
                            </template>
                        </CustomTooltip>
                    </div>
                </slot>
            </div>
        </div>
        <v-dialog
            v-model="mobilePreviewActive"
            transition="dialog-bottom-transition"
            @click:outside="mobilePreviewActive = false"
        >
            <v-card class="mobile-preview-modal">
                <div class="card-header m-0">
                    <div class="card-heading">Preview - "{{ title }}"</div>
                    <v-btn class="close-btn" outlined @click="mobilePreviewActive = false">
                        <font-awesome-icon icon="fa-solid fa-times" style="font-weight: 700"></font-awesome-icon>
                    </v-btn>
                </div>
                <div class="card-body">
                    <video :src="previewVideo" class="theme-preview" autoplay muted playsinline loop controls></video>
                </div>
                <v-card-actions class="mb-2">
                    <v-btn class="select-btn" @click="$emit('select')" v-if="!isSelected">
                        <font-awesome-icon icon="fa-regular fa-plus" class="mr-1"></font-awesome-icon>
                        Use this theme
                    </v-btn>
                    <div class="selected-block" v-else>Currently selected theme</div>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import CrownIcon from '@/assets/icons/crownIcon.vue';
import moment from 'moment';
import CustomTooltip from '@/components/ui/CustomTooltip.vue';
import ImageThumbnail from '@/components/Media/ImageThumbnail.vue';

export default {
    name: 'ThemeCard',
    components: {
        CrownIcon,
        CustomTooltip,
        ImageThumbnail,
    },
    props: {
        template: {
            type: Object,
            required: true,
        },
        isSelected: {
            type: Boolean,
            default() {
                return false;
            },
        },
        loading: {
            type: Boolean,
            default: false,
        },
        tributeEndingVideo: {
            type: [String, null],
            required: false,
            default() {
                return null;
            },
        },
        isSet: {
            type: [Boolean, String],
            default: true,
        },
    },
    inject: ['state'],
    data() {
        return {
            tooltipProps: { top: true },
            previewVideoLoading: false,
            previewVideoLoaded: false,
            hasMousedOver: false,
            mobilePreviewActive: false,
        };
    },
    computed: {
        currentTemplate() {
            return this.template;
        },
        title() {
            return this.template?.title;
        },
        duration() {
            return this.template?.duration;
        },
        thumbnail() {
            if (!this.template?.url) return null;
            const srcPath = this.template.url.split('/tribute-template-renders/')[1];
            const thumbNailMod = '/ik-thumbnail.jpg?tr=so-2';
            return `${process.env.VUE_APP_IMG_KIT_BASE}templates/${srcPath}${thumbNailMod}`;
        },
        previewVideo() {
            if (!this.template?.url) return null;
            const srcPath = this.template?.url.split('/tribute-template-renders/')[1];
            const previewMod = '?tr=so-2,q-50,w-400,ar-16-9';
            return `${process.env.VUE_APP_IMG_KIT_BASE}templates/${srcPath}${previewMod}`;
        },
        staffFavorite() {
            return this.template?.staffFavorite;
        },
        isCustomTheme() {
            // console.log(this.template);
            return this.template?.title === 'Custom Theme';
        },
    },
    methods: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(secs).padStart(2, '0');

            return `${formattedMinutes}:${formattedSeconds}`;
        },
        loadPreview() {
            this.previewVideoLoading = true;
        },
        previewLoaded() {
            this.previewVideoLoading = false;
            this.previewVideoLoaded = true;
        },
        play() {
            try {
                const video = this.$refs.videoPreview;
                if (video && this.$props.isSet) {
                    const playPromise = video.play();

                    if (playPromise !== undefined) {
                        playPromise.catch(error => {
                            if (error.name !== 'AbortError') {
                                console.warn('Unhandled video play error:', error);
                            }
                        });
                    }
                }
            } catch (error) {
                console.log(error, 'play on hover error');
            }
        },
        pause() {
            try {
                const video = this.$refs.videoPreview;
                if (video && !video.paused) {
                    video.pause();
                }
            } catch (error) {
                console.log(error, 'pause on hover end error');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.slide {
    width: 360px;
    height: 100%;
    max-height: 225px;
    border-radius: 12px;
    position: relative;
    transition: transform 0.2s, border 0.2s;
    flex-shrink: 0;
    display: inline-block;
    background: white;
    overflow: hidden;
    border: 1px solid #ccc;
    @include mobile() {
        border-radius: 8px;
    }
}

.timestamp-bar {
    display: flex;
    align-items: center;
    width: 90px;
    height: 35px;
    background: white;
    border: 1px solid #d1d5db;
    padding-top: 2px;
    padding-bottom: 2px;
    border-radius: 6px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    /* color: #6b7280; */
    padding-right: 12px;
    padding-left: 12px;
    letter-spacing: 2.5%;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
    .time-stamp {
        margin-left: 8px;
    }
    @include mobile() {
        width: fit-content;
        height: 24px;
        font-size: 12px;
        left: 6px;
        top: 6px;
        padding-left: 6px;
        padding-right: 6px;

        .time-stamp {
            margin-left: 4px;
        }
    }
}

.staff-picked {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 133.14px;
    height: 35px;
    padding: 9px 8px 9px 4px;
    border-radius: 54px 54px 0px 54px;
    background: #f97316;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    color: white;
    border: 1px solid #9a3412;
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    @include mobile() {
        width: fit-content;
        top: 6px;
        right: 6px;
        border-radius: 30px 30px 0px 30px;

        :v-deep {
            .crown-icon {
                width: 22px;
                height: 20px;
                transform: translateX(2px);
            }
        }
    }
}
// this ensures that hover autoplay isn't interupted when hovering on the overlays
.timestamp-bar,
.staff-picked {
    pointer-events: none;
}
.template-overlay {
    width: 100%;
    height: 54px;
    padding: 8px 16px;
    padding-right: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f9fafb;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 3;
    @include mobile() {
        height: 44px;
        padding: 8px;
    }
}

.slide-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: relative;
    img,
    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: relative;
    }
    .slide-preview {
        position: absolute;
        inset: 0;
        z-index: -1;
        height: auto;
        transition: opacity 250ms ease-in-out;
        opacity: 0;
    }
    &:hover .slide-preview {
        z-index: 1;
        opacity: 1;
    }
    &.unset {
        &:hover .slide-preview {
            opacity: 0 !important;
            z-index: -1 !important;
        }
        cursor: auto;
        &:before {
            background: darken($light-gray, 10%);
            content: '';
            position: absolute;
            inset: 0;
            z-index: 1;
        }
        &:after {
            content: 'Not set';
            z-index: 2;
            background: white;
            padding: 4px 6px;
            height: 35px;
            position: absolute;
            height: fit-content;
            width: fit-content;
            margin: auto;
            box-shadow: 0px 0px 0px rgba(0, 0, 0, 0.3);
            border-radius: 6px;
            left: 8px;
            top: 8px;
            border: 1px solid $light-gray;
            font-weight: bold;
            font-style: italic;
        }
    }
}

.template-title {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    text-align: left;
    color: #374151;
    line-height: 1.2;
    text-wrap-style: balance;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    display: -webkit-box;
    @include mobile() {
        line-height: 1;
        font-size: 13px;
    }
}

.button-group {
    display: flex;
    gap: 8px;
}

.template-button {
    width: 38px;
    height: 38px;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: white;
    border: 1px solid #d1d5db;
}

.template-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.white-button {
    background: white;
    border: 1px solid #d1d5db;
}

.orange-button {
    background: #fff7ed;
    border: 1px solid #fdba74;
    cursor: default;
}

.white-button:hover {
    border-color: #f97316;
}

.white-button:hover svg path {
    stroke: #f97316;
    fill: #f97316;
}
.slide-preview {
    position: absolute;
    /* inset: 0; */
    z-index: 0;
    width: 100%;
    height: 100%;
}

.spinner-container {
    position: absolute;
    inset: 0;
}
/* .preview:hover svg path {
    stroke: #f97316;
    fill: none;
} */

/* .preview:hover {
    color: #f97316;
} */

.mobile-preview-modal {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;

    .theme-preview {
        width: 100%;
        border-radius: 10px;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);

        @include landscape() {
            width: auto;
            max-height: min(60vh, 198px);
            margin: auto;
            align-self: center;
            display: flex;
        }
    }
    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .card-heading {
            flex-grow: 1;
            font-weight: bold;
            font-size: 0.95rem;
        }
        .close-btn {
            flex-shrink: 1;
            min-width: 40px;
            max-width: 40px;
            border-color: $medium-gray;
            color: $deep-gray;
        }
    }
    .v-card__actions {
        &::v-deep {
            display: flex;
            justify-content: center;
        }
    }
    .select-btn {
        text-transform: none;
        letter-spacing: normal;
        flex: 1;
        &::v-deep {
            background-color: $primary-orange !important;
        }
        color: white;
    }
    .selected-block {
        color: $deep-gray;
    }
}
.custom-theme {
    .icon-container {
        display: flex !important;
        margin: auto;
        width: 100%;
        height: calc(100% - 54px);
        position: relative;
        inset: 0;
        justify-content: center;
        align-items: center;
        z-index: 1;
        background: transparent;
        pointer-events: none;

        .custom-theme-icon {
            font-size: 3.8rem;
            color: #fff;
            filter: drop-shadow(2px 2px 30px black);
        }
    }
    .slide-preview {
        opacity: 1 !important;
        z-index: 0 !important;
    }
}
.overlay-extras {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    @include mobile() {
        top: 6px;
        right: 6px;
    }
    &:empty {
        display: none;
    }
}
</style>
